import React, { useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MuiLink from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Link as RouterLink } from 'react-router-dom';

import { Panel } from '../../../../../components/atoms/Panel';
import { ShipmentsPanelProps } from './PartsShipmentsPanel.props';
import { cellSx, headerSx, btnSx, formatDate } from './PartsShipmentsHelpers';
import { LinkShipmentPopover } from './LinkShipmentPopover';
import { CreateShipmentForm } from './CreateShipmentForm';

export const PartsShipmentsPanel: React.FC<ShipmentsPanelProps> = ({
  serviceId = 0, sourceNodeId = 0, clientHierarchy, clientContractIds,
  clientName, contractName, sourceName = '', destinationName = '',
  shipments = [], onRefresh,
}) => {
  const linkRef = useRef<HTMLButtonElement>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <Panel title="Shipments">
      {shipments.length > 0 ? (
        <TableContainer><Table size="small">
          <TableHead><TableRow>
            <TableCell sx={headerSx}>Shipment ID</TableCell>
            <TableCell sx={headerSx}>Created</TableCell>
            <TableCell sx={headerSx}>Source</TableCell>
            <TableCell sx={headerSx}>Destination</TableCell>
            <TableCell sx={headerSx}>Created By</TableCell>
          </TableRow></TableHead>
          <TableBody>{shipments.map((s, idx) => (
            <TableRow
              key={String(s.id ?? idx)}
              sx={{
                bgcolor: idx % 2 === 0 ? 'transparent' : 'v4.tableEven',
                '&:hover': { bgcolor: 'v4.tableHover' },
              }}
            >
              <TableCell sx={cellSx}>
                <MuiLink component={RouterLink} to={`/shipments/${s.id}`}
                  underline="hover" sx={{ fontSize: '12px', fontWeight: 500 }}>
                  {String(s.id)}
                </MuiLink>
              </TableCell>
              <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
                {formatDate(s.createdAt)}
              </TableCell>
              <TableCell sx={cellSx}>{s.source ?? '-'}</TableCell>
              <TableCell sx={cellSx}>{s.destination ?? '-'}</TableCell>
              <TableCell sx={cellSx}>{s.createdBy ?? '-'}</TableCell>
            </TableRow>
          ))}</TableBody>
        </Table></TableContainer>
      ) : (
        <Typography sx={{ fontSize: '13px', color: 'text.secondary', py: 2, textAlign: 'center' }}>
          No linked shipments
        </Typography>
      )}

      {!createOpen && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
          <Button size="small" variant="contained" onClick={() => setCreateOpen(true)} sx={btnSx}>
            Create New Shipment
          </Button>
          <Button ref={linkRef} size="small" variant="contained" onClick={() => setLinkOpen(true)} sx={btnSx}>
            Link Existing Shipment
          </Button>
        </Box>
      )}

      {createOpen && (
        <CreateShipmentForm
          serviceId={serviceId} sourceNodeId={sourceNodeId}
          sourceName={sourceName} destinationName={destinationName}
          clientHierarchy={clientHierarchy} clientContractIds={clientContractIds}
          clientName={clientName} contractName={contractName}
          onCreated={() => { setCreateOpen(false); onRefresh?.(); }}
          onCancel={() => setCreateOpen(false)}
        />
      )}

      <LinkShipmentPopover
        anchorEl={linkRef.current} open={linkOpen}
        onClose={() => setLinkOpen(false)}
        serviceId={serviceId} clientHierarchy={clientHierarchy}
        onLinked={() => onRefresh?.()}
      />
    </Panel>
  );
};
